import axios from "axios";

// Mismo-origen salvo que se defina una base explícita (igual que pl-fe).
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// 401 → sesión inválida (expiró o fue reemplazada por otro dispositivo):
// se limpia el token y App vuelve al login mediante el callback registrado.
let onAuthError = null;
export function setOnAuthError(fn) {
  onAuthError = fn;
}

api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || "";
    if (status === 401 && !url.startsWith("/auth/")) {
      localStorage.removeItem("access_token");
      if (onAuthError) onAuthError();
    }
    return Promise.reject(error);
  }
);

// ---- Sesión ----
export function getToken() {
  return localStorage.getItem("access_token");
}

export function logout() {
  localStorage.removeItem("access_token");
}

/**
 * Login del proveedor: email + contraseña.
 * Devuelve { mustChange: true } si el backend exige cambio de contraseña
 * (respuesta sin token), o { token } si el ingreso fue exitoso.
 */
export async function login(email, password) {
  const { data } = await api.post("/auth/login", {
    email: String(email).trim(),
    password,
  });
  if (data?.must_change_password) return { mustChange: true };
  const token = data?.access_token || data?.token;
  if (!token) throw new Error("Respuesta de login inválida");
  localStorage.setItem("access_token", token);
  return { token };
}

/** Cambio de contraseña forzado (sin token; valida con la contraseña actual). */
export async function changePassword(email, currentPassword, newPassword) {
  const { data } = await api.post("/auth/change-password", {
    email: String(email).trim(),
    current_password: currentPassword,
    new_password: newPassword,
  });
  return data;
}

// ---- Portal del proveedor (prefijo /api/sales) ----
export async function getMe() {
  const { data } = await api.get("/api/sales/provider/me");
  return data;
}

export async function getAvailability(date, originId, destinationId) {
  const { data } = await api.get("/api/sales/provider/availability", {
    params: { date, origin_id: originId, destination_id: destinationId },
  });
  return Array.isArray(data) ? data : data?.results || data?.items || [];
}

export async function createReservation(payload) {
  const { data } = await api.post("/api/sales/provider/reservations", payload);
  return data;
}

export async function listReservations(params = {}) {
  const { data } = await api.get("/api/sales/provider/reservations", { params });
  if (Array.isArray(data)) return data;
  return data?.items || data?.results || data?.reservations || [];
}

export async function getReservation(code) {
  const { data } = await api.get(
    `/api/sales/provider/reservations/${encodeURIComponent(code)}`
  );
  return data;
}

export async function cancelReservation(code, reason) {
  const { data } = await api.post(
    `/api/sales/provider/reservations/${encodeURIComponent(code)}/cancel`,
    { reason: reason || null }
  );
  return data;
}

/** Envía/reenvía el voucher por correo. Sin email usa el contacto del cupón. */
export async function sendVoucherEmail(code, email) {
  const { data } = await api.post(
    `/api/sales/provider/reservations/${encodeURIComponent(code)}/send-email`,
    { email: email || null }
  );
  return data;
}

export async function getMovements(params = {}) {
  const { data } = await api.get("/api/sales/provider/movements", { params });
  return data;
}

export default api;
